import { LitElement, css, customElement, html, state } from 'lit-element';

import TurndownService from 'turndown';
import { detectMobileViewport } from '../../helpers/device';
import { getMainPage } from '../../helpers/page-name';
import { marked } from 'marked';
import { property } from 'lit/decorators';

@customElement('rich-text-editor')
export class RichTextEditor extends LitElement {


    @property({ type: Boolean })
    isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    @property({ type: Boolean })
    allowEmbed = true;

    @property({ type: Boolean })
    allowPersonalization = false;

    @property({ type: Boolean })
    showSave = false;

    @state()
    private content: string = '';

    @state()
    isURLElementOpen: boolean = false;

    @state()
    newLink: string = '';

    @state()
    save: boolean = false;

    @state()
    currentHover: string | null = null;

    @state()
    showWarning: boolean = true;

    @state()
    personalizationOptions: Array<{ value: string; name: string }>  =  [];

    @state()
    public root: Element | null = null;

    async firstUpdated() {
        if (this.parentElement === null) {
            return;
        }
        const elem = this.parentElement.querySelector('rich-text-editor template');
        this.content = elem?.innerHTML ?? '';

        this.reset();

        this.dispatchEvent(new CustomEvent('ready'));
    }

    // puts into form for storing as string
    public getInnerText(): string {
        const turndownService = new TurndownService();
        // Configure Turndown to not add extra separators
        turndownService.addRule('hr', {
            filter: 'hr',
            replacement: () => ''
        });
        let text = turndownService.turndown(this.root?.innerHTML ?? '');
        // Clean up empty link artifacts and excessive whitespace
        text = text
            .replace(/\[[\s]*\]\(.*?\)/g, '') // Remove empty links like [  ](url)
            .replace(/\n{3,}/g, '\n\n') // Collapse multiple newlines to max 2
            .trim();

        return text;
    }

    // puts string in format to display in editor
    public setInnerText(text: string) {
        if (this.root) {
            this.root.innerHTML = marked.parse(text);
        }
    }

    public setInnerHtmlText(text: string) {
        if (this.root) {
            this.root.innerHTML = text;
        }
    }

    public getInnerHtmlText(): string {
        return this.root?.innerHTML ?? '';
    }

    public getInnerCharCount(): number {
        return this.root?.textContent?.length ?? 0;
    }

    reset() {
        const parser = new DOMParser();
        const doc = parser.parseFromString(this.content, 'text/html');
        document.execCommand('defaultParagraphSeparator', false, 'br');
        document.addEventListener('selectionchange', () => {
            this.requestUpdate();
        });
        const root = doc.querySelector('body');
        if (root === null) {
            return;
        }
        root.setAttribute('contenteditable', 'true');
        this.root = root;
    }

    getSelection(): Selection | null {
        try {
            if (this.shadowRoot && typeof (this.shadowRoot as any).getSelection === 'function') {
                return (this.shadowRoot as any).getSelection();
            }
        } catch (e) {
            console.error(e);
        }
        // fallback for Safari
        if (typeof document.getSelection === 'function') {
            return document.getSelection();
        }
        return null;
    }

    render() {
        return html`
            <main>
                ${!this.isSafari ? html`
                    <div id="editor-actions">
                        <div id="toolbar">
                            ${this.toolbar(this.getSelection(), (command, val) => {
                                document.execCommand(command, false, val);
                            })}
                        </div>
                        <span class="helperText">${this.currentHover ?? ''}</span>
                    </div>
                ` : ''}
                <div id="editor">${this.root}</div>
                ${this.isSafari && this.showWarning && !detectMobileViewport() ? html`
                    <div class="safari-warning">
                        Text customizations like bolding and linking are not available in Safari, please use another browser to style your description text
                    </div>
                ` : ''}
                <error-toast id="errorToast"></error-toast>
            </main>
            ${this.showSave ? html`
                <form-button size="xs" variant="outline" @click="${this.dispatchSaveEvent}" class="form-button">Save Text Changes</form-button>
            ` : ''}
            `;
    }

    dispatchSaveEvent() {
        const content = this.getInnerText();
        this.dispatchEvent(new CustomEvent('save', { detail: { content } }));
        getMainPage().errorToastElement.showToast('Saved');
    }

    private showPersonalizationMenu() {
        // Get the button that was clicked using shadowRoot instead of renderRoot
        const button = this.shadowRoot?.querySelector('mwc-icon-button[icon="account_box"]');
        if (!button) {
            console.error('Button not found in shadow DOM');
            return;
        }

        // Create dropdown container
        const dropdown = document.createElement('div');
        dropdown.className = 'personalization-dropdown';

        // Use default options if none provided
        const options = this.personalizationOptions.length > 0 ?
            this.personalizationOptions : [];
        // Create option buttons
        options.forEach(option => {
            const optionButton = document.createElement('button');
            optionButton.textContent = option.name;
            optionButton.className = 'personalization-option';
            optionButton.onclick = () => {
                document.execCommand('insertText', false, option.value);
                dropdown.remove();
            };
            dropdown.appendChild(optionButton);
        });

        // Position directly below the button in the editor toolbar
        // Get the button's position relative to its parent
        const toolbar = this.shadowRoot?.querySelector('#toolbar');
        if (!toolbar) {
            console.error('Toolbar not found');
            return;
        }

        // Position the dropdown relative to the toolbar
        dropdown.style.position = 'absolute';
        dropdown.style.top = '34px'; // Height of the toolbar
        dropdown.style.left = '200px'; // Position roughly where the personalization button is
        dropdown.style.zIndex = '1000';

        // Add dropdown to the shadow DOM
        toolbar.appendChild(dropdown);
    }

    static get styles() {
        return css`
            :host {
                --editor-width: 100%;
                --editor-toolbar-height: 34px;
                --editor-toolbar-background: var(--main-primary-color);
                --editor-toolbar-on-background: var(--theme-white-color);
                --editor-toolbar-on-active-background: var(--theme-focus-border-color);
                display: block;
                overflow-y: auto;
                position: relative;
            }
            main {
                display: block;
                height: var(--editor-height);
                border: 1px solid var(--theme-input-border-color);
                display: flex;
                flex-direction: column;
                border-radius: 5px;
            }

            .personalization-dropdown {
                background-color: var(--main-primary-color);
                border: 1px solid var(--theme-input-border-color);
                border-radius: 4px;
                box-shadow: 0 2px 8px rgba(0,0,0,0.2);
                z-index: 1000;
                padding: 5px 0;
                min-width: 180px;
                max-width: 250px;
            }

            .personalization-option {
                display: block;
                width: 100%;
                padding: 8px 12px;
                text-align: left;
                background: none;
                border: none;
                cursor: pointer;
                font-size: 14px;
                color: white;
            }

            .personalization-option:hover {
                background-color: var(--theme-focus-border-color);
            }

            .helperText {
                font-size: 12px;
                color: var(--editor-toolbar-on-background);
                margin-right: 10px;
                line-height: 14px;
            }
            #editor-actions {
                grid-area: toolbar;
                width: var(--editor-width);
                height: var(--editor-toolbar-height);
                background-color: var(--editor-toolbar-background);
                color: var(--editor-toolbar-on-background);
                overscroll-behavior: contain;
                overflow-y: auto;
                -ms-overflow-style: none;
                scrollbar-width: none;
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: space-between;
            }
            #editor-actions::-webkit-scrollbar {
                display: none;
            }
            #editor {
                width: var(--editor-width);
                grid-area: editor;
                background-color: var(--editor-background);
                box-sizing: border-box;
                padding: 4px 10px;
            }
            #toolbar {
                width: 100%;
                height: var(--editor-toolbar-height);
                padding: 2px 0px;
            }
            [contenteditable] {
                outline: 0px solid transparent;
            }

            .safari-warning {
                margin-top: 10px;
                font-size: 12px;
                line-height: 14px;
            }
            #toolbar > mwc-icon-button {
                color: var(--editor-toolbar-on-background);
                --mdc-icon-size: 20px;
                --mdc-icon-button-size: 30px;
                cursor: pointer;
            }
            #toolbar > .active {
                color: var(--editor-toolbar-on-active-background);
            }
            select {
                margin-top: 5px;
                height: calc(var(--editor-toolbar-height) - 10px);
            }
            input[type='color'] {
                height: calc(var(--editor-toolbar-height) - 15px);
                -webkit-appearance: none;
                border: none;
                width: 22px;
            }
            input[type='color']::-webkit-color-swatch-wrapper {
                padding: 0;
            }
            input[type='color']::-webkit-color-swatch {
                border: none;
            }
        `;
    }

    toolbar(
        selection: Selection | null,
        command: (c: string, val: string | undefined) => void
    ) {
        const tags: string[] = [];
        if (selection?.type === 'Range') {
            // @ts-expect-error
            let parentNode = selection?.baseNode;
            if (parentNode !== undefined) {
                const checkNode = () => {
                    const parentTagName = parentNode?.tagName?.toLowerCase()?.trim();
                    if (parentTagName !== undefined) tags.push(parentTagName);
                };
                while (parentNode != null) {
                    checkNode();
                    parentNode = parentNode?.parentNode;
                }
            }
        }
        const commands: Array<{
            icon: string;
            command: string | (() => void);
            active?: boolean;
            show?: boolean;
            type?: string;
            values?: Array<{ value: string; name: string; font?: boolean }>;
            command_value?: string;
            action: string;
        }> = [
            {
                icon: 'filter_1',
                command: 'formatBlock',
                command_value: tags.includes('h1') ? 'p' : '<h1>',
                active: tags.includes('h1'),
                action: 'Header 1',
            },
            {
                icon: 'filter_2',
                command: 'formatBlock',
                command_value: tags.includes('h2') ? 'p' : '<h2>',
                active: tags.includes('h2'),
                action: 'Header 2',
            },
            {
                icon: 'format_bold',
                command: 'bold',
                active: tags.includes('b'),
                action: 'Bold Text',
            },
            {
                icon: 'format_italic',
                command: 'italic',
                active: tags.includes('i'),
                action: 'Italic Text',
            },
            tags.includes('a')
                ? { icon: 'link_off', command: 'unlink', action: 'Remove Link' }
                : {
                    icon: 'add_link',
                    action: 'Add Link',
                    command: () => {
                        // if selection is null and command is link or embed, put cursor at the end of the text and select it
                        if (selection === null || selection?.type === 'None'){
                            const range = document.createRange();
                            range.setStart(this.root as Node, 0);
                            range.setEnd(this.root as Node, 0);
                            selection = document.getSelection();
                            selection?.removeAllRanges();
                            selection?.addRange(range);
                        }
                        const newLink = prompt('Write the URL here', 'http://');
                        if (newLink && newLink !== '' && newLink !== 'http://') {
                            command('createlink', newLink);
                        }
                    },
                },
            {
                icon: 'subtitles',
                action: 'Embed Link as Card',
                command: () => {
                    // if selection is null and command is link or embed, put cursor at the end of the text and select it
                    if (selection === null || selection?.type === 'None'){
                        const range = document.createRange();
                        range.setStart(this.root as Node, 0);
                        range.setEnd(this.root as Node, 0);
                        selection = document.getSelection();
                        selection?.removeAllRanges();
                        selection?.addRange(range);
                    }
                    let newEmbedLink = prompt('Write the URL you would like to embed here', 'http://');
                    if (newEmbedLink && newEmbedLink !== '' && newEmbedLink !== 'http://') {
                        // add proxi_embed = true to the URL
                        if (newEmbedLink.includes('?')) {
                            // If the URL already has a query string, append the parameter with &
                            newEmbedLink += '&proxi_embed=true';
                        } else {
                            // If the URL doesn't have a query string, append the parameter with ?
                            newEmbedLink += '?proxi_embed=true';
                        }
                        command('createlink', newEmbedLink);
                    }
                },
                show: this.allowEmbed,
            },
            {
                icon: 'account_box',
                action: 'Add Personalization',
                command: () => {
                    // if selection is null and command is link or embed, put cursor at the end of the text and select it
                    if (selection === null || selection?.type === 'None'){
                        const range = document.createRange();
                        range.setStart(this.root as Node, 0);
                        range.setEnd(this.root as Node, 0);
                        selection = document.getSelection();
                        selection?.removeAllRanges();
                        selection?.addRange(range);
                    }

                    // Close any existing dropdown
                    const existingDropdown = this.renderRoot.querySelector('.personalization-dropdown');
                    if (existingDropdown) {
                        existingDropdown.remove();
                    }

                    // Create and show the dropdown
                    this.showPersonalizationMenu();
                },
                show: this.allowPersonalization && this.personalizationOptions.length > 0,
            },
            {
                icon: 'undo',
                command: 'undo',
                action: 'Undo',
            },
            {
                icon: 'redo',
                command: 'redo',
                action: 'Redo',
            },
        ];

        return html`
            ${commands.filter(c => c.show !== false).map((n) => {
                const elem = document.createElement('mwc-icon-button');
                elem.setAttribute('icon', n.icon);
                elem.className = (n.active === true) ? 'active' : 'inactive';
                elem.addEventListener('click', () => {
                    if (typeof n.command === 'string') {
                        command(n.command, n.command_value);
                    } else {
                        n.command();
                    }
                });
                elem.addEventListener('mouseover', () => {
                    this.currentHover = n.action;
                });
                elem.addEventListener('mouseout', () => {
                    this.currentHover = null;
                });

                return html`
                    ${n.values
                    ? html`
                        <select
                            id='${n.icon}'
                            @change=${(e: any) => {
                                const val = e.target.value;
                                if (typeof n.command === 'string') {
                                    command(n.command, val);
                                }
                            }}
                            >
                            ${n.values.map(
                                (v) => html` <option value=${v.value}>${v.name}</option>`
                            )}
                        </select>`
                    : html` ${elem}`}
                `;
            })}
        `;
    }
}

